import {Button, Checkbox, Separator} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useState} from 'react';

import {isEmpty, isNil, isNotNil, omit} from 'ramda';

import {TelephonyCall, useGetTelephonyCallQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {customerRoutes, telephonyRoutes} from '@dms/routes';
import {
  formatCallDuration,
  useCanOpenTelephonyCall,
  useTelephonyCallRecordPairing,
} from '@dms/shared';

import {composePath, DOT_CHARACTER, parseDate, suffixTestId, useNavigate} from 'shared/web';

import {useCallCustomerName} from '../../../hooks/useCallCustomerName';
import {useCallPanel} from '../../../hooks/useCallPanel';
import {usePairableCall} from '../../../hooks/usePairableCall';
import {getCallPhoneNumberLabel} from '../../../utils/getCallPhoneNumberLabel';
import {isTelephonyCallNotFound, isUnkeptTelephonyCall} from '../../../utils/isUnkeptTelephonyCall';
import {ForgetPairableCall} from '../../TelephonyCallAfterCall/components/ForgetPairableCall';
import {CallCustomerActions} from '../../TelephonyCallIndicator/components/CallCustomerActions';
import {TelephonyCallNoteEditor} from './TelephonyCallNoteEditor';
import {TelephonyCallPanelCaller} from './TelephonyCallPanelCaller';

/** Pairings and the note push nothing; another tab's changes, and a lost end push, arrive with this re-read. */
const CALL_REREAD_MS = 3000;
const TEST_ID = 'telephonyCallPanel';

const getRecordKey = (record: {resourceId: string; recordId: string}) =>
  `${record.resourceId}-${record.recordId}`;

interface TelephonyCallPanelPairingProps {
  callId: string;
  /** Ends the after-call state and closes the panel. */
  onDone: VoidFunction;
}

/**
 * The viewer's own call in the panel: records to pair (the one on screen first, then those opened
 * since the call started) and the note. Both save at once and stay editable after the call, when
 * the caller moves here from the live part and Done ends the after-call state.
 */
export function TelephonyCallPanelPairing(props: TelephonyCallPanelPairingProps) {
  const {data: call, error} = useGetTelephonyCallQuery(
    {callId: props.callId},
    {refetchOnMountOrArgChange: true, pollingInterval: CALL_REREAD_MS, skipPollingIfUnfocused: true}
  );

  // A call the backend did not keep has nothing left to pair or note.
  if (isTelephonyCallNotFound(error) || (isNotNil(call) && isUnkeptTelephonyCall(call))) {
    return <ForgetPairableCall callId={props.callId} />;
  }

  if (isNil(call)) {
    return null;
  }

  return <PairingContent call={call} onDone={props.onDone} />;
}

/** A record row: opened in this browser during the call, or paired elsewhere. */
interface PanelRow {
  resourceId: string;
  recordId: string;
  title: string;
  details: string[];
  customerName: string | null;
}

interface PairingContentProps {
  call: TelephonyCall;
  onDone: VoidFunction;
}

function PairingContent(props: PairingContentProps) {
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();
  const {currentRecordKey} = useCallPanel();
  const {state: pairable} = usePairableCall();
  const {pair, unpair} = useTelephonyCallRecordPairing();
  const canOpenCall = useCanOpenTelephonyCall();
  // Ticks the viewer made that the call's re-read does not show yet, by record key.
  const [pendingTicks, setPendingTicks] = useState<Record<string, boolean>>({});

  const isEnded = props.call.status === 'ended';
  // Notes and pairings wait for the connected call: before it, they could be dropped with it.
  const canEdit = props.call.status === 'in_call' || (isEnded && isNotNil(props.call.connectedAt));
  const pairedKeys = props.call.records.map(getRecordKey);
  const confirmedTicks = Object.keys(pendingTicks).filter(
    (key) => pendingTicks[key] === pairedKeys.includes(key)
  );

  if (!isEmpty(confirmedTicks)) {
    setPendingTicks(omit(confirmedTicks, pendingTicks));
  }

  const openedRows = (pairable?.records ?? []).map((record): PanelRow => ({
    ...record,
    details: [
      ...record.details,
      i18n.t('page.telephony.afterCall.openedAt', {
        time: formatDateTime('timeShort', parseDate(record.openedAt)),
      }),
    ],
  }));
  const openedKeys = openedRows.map(getRecordKey);
  // Paired from the thread, another device or a colleague: the call knows only the record's kind.
  const pairedElsewhereRows = props.call.records
    .filter((record) => !openedKeys.includes(getRecordKey(record)))
    .map((record): PanelRow => ({
      ...record,
      title: i18n.t(`entity.customerMessage.labels.contextResource.${record.resourceId}`),
      details: [i18n.t('page.telephony.panel.pairedElsewhere')],
      customerName: null,
    }));
  const currentRow = openedRows.find((row) => getRecordKey(row) === currentRecordKey);
  const otherRows = [...openedRows.filter((row) => row !== currentRow), ...pairedElsewhereRows];
  const callerName = useCallCustomerName(props.call) ?? getCallPhoneNumberLabel(props.call);

  const handleToggle = (record: PanelRow, isChecked: boolean) => {
    const pairingRecord = {resourceId: record.resourceId, recordId: record.recordId};
    const mismatchMessage = isNotNil(record.customerName)
      ? i18n.t('page.telephony.pairing.customerMismatchNamed', {
          callCustomer: callerName,
          recordCustomer: record.customerName,
        })
      : undefined;

    const key = getRecordKey(record);

    setPendingTicks((ticks) => ({...ticks, [key]: isChecked}));
    (isChecked
      ? pair(props.call.id, pairingRecord, mismatchMessage)
      : unpair(props.call.id, pairingRecord)
    ).then((isChanged) => {
      // Refused (another customer's record): the tick goes back to what the call has.
      if (!isChanged) {
        setPendingTicks((ticks) => omit([key], ticks));
      }
    });
  };

  const renderRow = (record: PanelRow, isOnScreen: boolean) => {
    const key = getRecordKey(record);
    const isPending = key in pendingTicks;
    const isPaired = isPending ? pendingTicks[key] : pairedKeys.includes(key);

    return (
      <HStack key={key} spacing={3} align="center">
        <Checkbox
          value={isPaired}
          isDisabled={!canEdit || isPending}
          onChange={(isChecked) => handleToggle(record, isChecked)}
          aria-label={record.title}
          data-testid={suffixTestId(`record-[${record.recordId}]`, {'data-testid': TEST_ID})}
        />
        <VStack grow={1}>
          <HStack spacing={2} align="center">
            <Text overflowWrap="anywhere">{record.title}</Text>
            <Show when={isOnScreen}>
              <Text size="xSmall" color="link">
                {i18n.t('page.telephony.panel.youAreHere')}
              </Text>
            </Show>
          </HStack>
          <Text size="xSmall" color="secondary" overflowWrap="anywhere">
            {record.details.join(` ${DOT_CHARACTER} `)}
          </Text>
        </VStack>
        <Show when={isPaired}>
          <Text size="xSmall" color="success">
            {i18n.t('page.telephony.panel.paired')}
          </Text>
        </Show>
      </HStack>
    );
  };

  return (
    <>
      <Show when={isEnded}>
        <TelephonyCallPanelCaller
          call={props.call}
          status={i18n.t('page.telephony.panel.ended', {
            duration: formatCallDuration(props.call.durationSeconds ?? 0),
          })}
          dotColor="palettes.neutral.300.100"
          actions={
            <>
              <CallCustomerActions
                call={props.call}
                onOpenCustomer={(customerId) =>
                  navigate(composePath(customerRoutes.detail, {params: {id: customerId}}))
                }
                onCreateCustomer={() => navigate(customerRoutes.create)}
                onFindCustomer={() => navigate(customerRoutes.overview)}
                data-testid={TEST_ID}
              />
              <Show when={canOpenCall}>
                <Button
                  variant="secondary"
                  size="small"
                  leftIcon="action/open_in_new"
                  title={i18n.t('page.telephony.panel.openCall')}
                  onClick={() =>
                    navigate(composePath(telephonyRoutes.callDetail, {params: {id: props.call.id}}))
                  }
                  data-testid={suffixTestId('openCall', {'data-testid': TEST_ID})}
                />
              </Show>
            </>
          }
        />
        <Separator spacing={0} />
      </Show>
      <Box padding={4}>
        <VStack spacing={4}>
          <Show when={!canEdit}>
            <Text size="small" color="secondary">
              {i18n.t('page.telephony.panel.notConnected')}
            </Text>
          </Show>
          <Show when={currentRow}>
            {(record) => (
              <VStack spacing={2}>
                <Text size="xSmall" color="secondary" alternative>
                  {i18n.t('page.telephony.panel.thisPage')}
                </Text>
                {renderRow(record, true)}
              </VStack>
            )}
          </Show>
          <VStack spacing={2}>
            <Text size="xSmall" color="secondary" alternative>
              {i18n.t('page.telephony.afterCall.openedDuringCall')}
            </Text>
            <Show when={isEmpty(otherRows)}>
              <Text size="small" color="secondary">
                {i18n.t('page.telephony.panel.noOtherRecords')}
              </Text>
            </Show>
            {otherRows.map((record) => renderRow(record, false))}
          </VStack>
          <TelephonyCallNoteEditor
            callId={props.call.id}
            serverNote={props.call.note}
            isDisabled={!canEdit}
            data-testid={TEST_ID}
          />
          <HStack spacing={2} align="center" justify="space-between">
            <Text size="xSmall" color="secondary">
              {isEnded
                ? i18n.t('page.telephony.afterCall.laterHint')
                : i18n.t('page.telephony.panel.liveHint')}
            </Text>
            <Show when={isEnded}>
              <Button
                variant="primary"
                title={i18n.t('page.telephony.panel.done')}
                onClick={props.onDone}
                data-testid={suffixTestId('done', {'data-testid': TEST_ID})}
              />
            </Show>
          </HStack>
        </VStack>
      </Box>
    </>
  );
}
